import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';
import { LoyaltyMember, LoyaltyTierConfig } from '../queries/loyalty.queries';

// ================================
// OTP MUTATIONS
// ================================

export interface SendOTPInput {
  phone: string;
  purpose: 'registration' | 'login' | 'password_reset' | 'phone_verify';
}

export interface VerifyOTPInput {
  phone: string;
  otp: string;
  purpose: 'registration' | 'login' | 'password_reset' | 'phone_verify';
}

/**
 * Send OTP for registration or verification
 */
export function useSendOTP() {
  return useMutation({
    mutationFn: async (input: SendOTPInput) => {
      const response = await apiClient.post<
        APIResponse<{
          message: string;
          expiresIn: number;
          otp?: string; // Only in dev mode
        }>
      >('/loyalty/otp/send', input);
      return response.data.data;
    },
  });
}

/**
 * Verify OTP
 */
export function useVerifyOTP() {
  return useMutation({
    mutationFn: async (input: VerifyOTPInput) => {
      const response = await apiClient.post<
        APIResponse<{
          verified: boolean;
          phone: string;
          purpose: string;
        }>
      >('/loyalty/otp/verify', input);
      return response.data.data;
    },
  });
}

// ================================
// REGISTRATION MUTATIONS
// ================================

export interface RegisterMemberInput {
  phone: string;
  name: string;
  email?: string;
  birthdate?: string;
  gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  nickname?: string;
  referralCode?: string;
  registrationSource?: 'pos_scan' | 'mobile_app' | 'web' | 'referral' | 'import';
  branchId?: string;
  language?: string;
  smsOptIn?: boolean;
  emailOptIn?: boolean;
}

export interface QuickRegisterInput {
  phone: string;
  name: string;
  branchId: string;
}

/**
 * Register new loyalty member (with OTP verification)
 */
export function useRegisterMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: RegisterMemberInput) => {
      const response = await apiClient.post<
        APIResponse<{
          member: LoyaltyMember;
          tierConfig: LoyaltyTierConfig | null;
          referralApplied: boolean;
        }>
      >('/loyalty/register', input);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'members'] });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'customers'] });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'analytics'] });
    },
  });
}

/**
 * Quick registration at POS (staff-assisted, no OTP)
 */
export function useQuickRegister() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: QuickRegisterInput) => {
      const response = await apiClient.post<
        APIResponse<{
          member: LoyaltyMember;
          tierConfig: LoyaltyTierConfig | null;
          isExisting: boolean;
        }>
      >('/loyalty/quick-register', input);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'members'] });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'customers'] });
    },
  });
}

// Legacy alias
export function useRegisterCustomer() {
  return useQuickRegister();
}

// ================================
// MEMBER UPDATE MUTATIONS
// ================================

export interface UpdateMemberInput {
  name?: string;
  email?: string;
  birthdate?: string;
  gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  nickname?: string;
  smsOptIn?: boolean;
  emailOptIn?: boolean;
  pushOptIn?: boolean;
  language?: string;
  notes?: string;
  tags?: string[];
}

/**
 * Update member profile
 */
export function useUpdateMember(memberId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateMemberInput) => {
      const response = await apiClient.patch<APIResponse<{ member: LoyaltyMember }>>(
        `/loyalty/member/${memberId}`,
        input
      );
      return response.data.data.member;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'member', memberId] });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'members'] });
    },
  });
}

// ================================
// QR & POINTS EARNING MUTATIONS
// ================================

export interface GenerateQRInput {
  orderId: string;
  customerId: string; // Phone number
  branchId: string;
  amount: number;
}

export interface ScanQRInput {
  token: string;
  branchId: string;
  deviceFingerprint?: string;
}

export interface EarnPointsInput {
  memberId: string;
  orderId?: string;
  branchId: string;
  amount: number;
  description?: string;
}

/**
 * Generate single-use QR token for loyalty points
 */
export function useGenerateQR() {
  return useMutation({
    mutationFn: async (input: GenerateQRInput) => {
      const response = await apiClient.post<
        APIResponse<{
          token: string;
          qrTokenId: string;
          points: number;
          earnRate: number;
          multiplier: number;
          expiresAt: string;
        }>
      >('/loyalty/generate-qr', input);
      return response.data.data;
    },
  });
}

/**
 * Scan QR code to redeem loyalty points
 */
export function useScanQR() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ScanQRInput) => {
      const response = await apiClient.post<
        APIResponse<{
          member: LoyaltyMember;
          pointsAdded: number;
          previousBalance: number;
          newBalance: number;
          tierUpgraded: boolean;
          newTier: string;
        }>
      >('/loyalty/scan', input);
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['loyalty'] });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'member', data.member._id] });
    },
  });
}

/**
 * Directly award points (without QR)
 */
export function useEarnPoints() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: EarnPointsInput) => {
      const response = await apiClient.post<
        APIResponse<{
          member: LoyaltyMember;
          pointsEarned: number;
          previousBalance: number;
          newBalance: number;
          tierUpgraded: boolean;
          newTier: string;
        }>
      >('/loyalty/earn', input);
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['loyalty'] });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'member', data.member._id] });
    },
  });
}

// ================================
// POINTS REDEMPTION MUTATIONS
// ================================

export interface RedeemPointsInput {
  memberId: string;
  points: number;
  orderId?: string;
  branchId: string;
}

export interface AdjustPointsInput {
  memberId: string;
  points: number; // Can be positive or negative
  reason: string;
  branchId?: string;
}

/**
 * Redeem loyalty points for discount
 */
export function useRedeemPoints() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: RedeemPointsInput) => {
      const response = await apiClient.post<
        APIResponse<{
          member: LoyaltyMember;
          pointsRedeemed: number;
          discountValue: number;
          remainingPoints: number;
          redeemRate: number;
        }>
      >('/loyalty/redeem', input);
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['loyalty'] });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'member', data.member._id] });
    },
  });
}

/**
 * Manually adjust points (admin only)
 */
export function useAdjustPoints() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AdjustPointsInput) => {
      const response = await apiClient.post<
        APIResponse<{
          member: LoyaltyMember;
          adjustment: number;
          previousBalance: number;
          newBalance: number;
        }>
      >('/loyalty/adjust', input);
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['loyalty'] });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'member', data.member._id] });
    },
  });
}

// ================================
// BIRTHDAY REWARD MUTATIONS
// ================================

export interface ClaimBirthdayInput {
  memberId: string;
  branchId: string;
}

/**
 * Claim birthday reward
 */
export function useClaimBirthdayReward() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ClaimBirthdayInput) => {
      const response = await apiClient.post<
        APIResponse<{
          member: LoyaltyMember;
          birthdayBonus: number;
          previousBalance: number;
          newBalance: number;
          hasFreeItem: boolean;
        }>
      >('/loyalty/birthday/claim', input);
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['loyalty'] });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'member', data.member._id] });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'birthday', 'eligible'] });
    },
  });
}

// ================================
// TIER MANAGEMENT MUTATIONS
// ================================

export interface TierConfigInput {
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';
  name: {
    en: string;
    th?: string;
    zh?: string;
  };
  minLifetimePoints: number;
  minLifetimeSpend?: number;
  minOrdersCount?: number;
  earnRate: number;
  earnMultiplier?: number;
  redeemRate: number;
  birthdayBonus: number;
  birthdayMultiplier?: number;
  welcomeBonus?: number;
  monthlyBonus?: number;
  discountPercentage?: number;
  freeUpgradeCount?: number;
  prioritySupport?: boolean;
  exclusivePromotions?: boolean;
  earlyAccess?: boolean;
  freeDelivery?: boolean;
  birthdayFreeItem?: boolean;
  pointsExpirationMonths?: number;
  gracePeriodonths?: number;
  color?: string;
  iconUrl?: string;
  badgeUrl?: string;
  sortOrder?: number;
}

/**
 * Create or update tier configuration
 */
export function useUpsertTier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: TierConfigInput) => {
      const response = await apiClient.post<APIResponse<{ tier: LoyaltyTierConfig }>>(
        '/loyalty/tiers',
        input
      );
      return response.data.data.tier;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'tiers'] });
    },
  });
}

/**
 * Deactivate a tier
 */
export function useDeactivateTier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tier: string) => {
      const response = await apiClient.delete<APIResponse<{ message: string }>>(
        `/loyalty/tiers/${tier}`
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'tiers'] });
    },
  });
}

// ================================
// LEGACY EXPORTS (backward compatibility)
// ================================

export interface RegisterCustomerInput {
  name: string;
  phone: string;
  email?: string;
}

export type { LoyaltyMember as Customer };
